import { z } from 'zod';
import Authority from '@app/model/authentication/Authority';
import ACLAuthority from '@app/model/profile/ACLAuthority';
import Domain from '@app/model/_common/Domain';
import { CompanyProfileOutput } from '@app/model/company_profile/output/CompanyProfileOutput';
import CompanyProfileInput from '@app/model/company_profile/input/CompanyProfileInput';
import { ProfileCompanyHotelAccessControl } from '@app/model/profile/ProfileCompanyHotelAccessControl';
import AccessControlInput from '@app/model/company_profile/input/AccessControlInput';
import _isEmpty from 'lodash/isEmpty';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';

const mapAclForCompanyHotelId = (companyHotelId: number, aclOutputs: ProfileCompanyHotelAccessControl[]): AccessControlInput => {
	const aclOutput = aclOutputs.find(aclOutput => aclOutput.companyHotelId === companyHotelId && !_isEmpty(aclOutput.domainAuthorities));
	if (!!aclOutput) {
		return {
			companyHotelId: companyHotelId,
			domainAuthorities: Object.values(Domain).map(domain => ({
				domain,
				authority: aclOutput.domainAuthorities.find(da => da.domain === domain)?.authority ?? ACLAuthority.NONE,
			})),
		};
	} else {
		return newAclForCompanyHotelId(companyHotelId);
	}
};

const newAclForCompanyHotelId = (companyHotelId: number): AccessControlInput => {
	return {
		companyHotelId: companyHotelId,
		domainAuthorities: Object.values(Domain).map(domain => {
			return {
				domain: domain,
				authority: ACLAuthority.NONE,
			};
		}),
	};
};


export const useSaveCompanyProfileFormValidation = (companyProfile: CompanyProfileOutput | null, companyHotels: CompanyHotelLite[]) => {
	let saveCompanyProfileFormValidation = z.object({
		email: z.string().email('Email is required'),
		name: z.string().nonempty('Name is required'),
		authority: z.nativeEnum(Authority),
		pricingAuthority: z.nativeEnum(ACLAuthority),
		accessControl: z.array(z.object({
			companyHotelId: z.number(),
			domainAuthorities: z.array(z.object({
				domain: z.nativeEnum(Domain),
				authority: z.nativeEnum(ACLAuthority),
			})),
		})),
	});

	let companyProfileInputFormValues: CompanyProfileInput;

	if (!!companyProfile) {
		saveCompanyProfileFormValidation = saveCompanyProfileFormValidation.extend({
			profileId: z.number().min(1, 'Profile is required'),
		});

		companyProfileInputFormValues = {
			profileId: companyProfile.profile.id,
			pricingAuthority: companyProfile.profile.pricingAuthority ?? ACLAuthority.NONE,
			email: companyProfile.profile.email,
			name: companyProfile.profile.name,
			authority: companyProfile.profile.authorities.includes(Authority.ADMIN) ? Authority.ADMIN : Authority.USER,
			departmentId: companyProfile.profile.department?.id,
			accessControl: companyHotels.map(companyHotel => {
				return mapAclForCompanyHotelId(companyHotel.id, companyProfile.profile.accessControl);
			}),
		};
	} else {

		companyProfileInputFormValues = {
			email: '',
			name: '',
			authority: Authority.USER,
			pricingAuthority: ACLAuthority.NONE,
			accessControl: companyHotels.map(companyHotel => newAclForCompanyHotelId(companyHotel.id)),
		};
	}

	const refinedCompanyProfileInputSchema = saveCompanyProfileFormValidation.refine(
		(data) => {
			if (data.authority === Authority.USER) {
				return data.accessControl.some((acl) =>
					acl.domainAuthorities.some(da => da.authority !== ACLAuthority.NONE));
			}
			return true;
		},
		{
			message: 'A user must have at least one assigned property.',
			path: ['accessControl'],
		},
	);

	return { validationSchema: refinedCompanyProfileInputSchema, defaultValues: companyProfileInputFormValues };
};
